import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';

const pressableSelector = [
  '.button',
  '.link-button',
  '.brand-lockup',
  '.category-card',
  '.product-card',
  '.document-card',
  '.site-nav button',
  '.mobile-nav button',
  '.language-switcher button',
  '.menu-toggle',
  '.floating-app-bar button',
  '.floating-app-bar a',
].join(',');

function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.top < viewportHeight - 80 && rect.bottom > 80;
}

export default function AnimationController() {
  const location = useLocation();
  const { language } = useI18n();

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      document.documentElement.classList.add('reduce-motion');
      return undefined;
    }

    document.documentElement.classList.remove('reduce-motion');

    const revealElements = Array.from(document.querySelectorAll('[data-reveal]'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    revealElements.forEach((element, index) => {
      element.style.setProperty('--reveal-delay', `${Math.min((index % 8) * 70, 420)}ms`);

      if (element.classList.contains('is-visible')) {
        return;
      }

      if (isInViewport(element)) {
        window.requestAnimationFrame(() => {
          element.classList.add('is-visible');
        });
        return;
      }

      observer.observe(element);
    });

    const handlePointerMove = (event) => {
      const element = event.target.closest('[data-tilt]');
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;

      element.style.setProperty('--tilt-x', `${(-y * 5).toFixed(2)}deg`);
      element.style.setProperty('--tilt-y', `${(x * 5).toFixed(2)}deg`);
      element.style.setProperty('--glow-x', `${event.clientX - rect.left}px`);
      element.style.setProperty('--glow-y', `${event.clientY - rect.top}px`);
    };

    const handlePointerLeave = (event) => {
      const element = event.target.closest('[data-tilt]');
      if (!element) return;

      element.style.setProperty('--tilt-x', '0deg');
      element.style.setProperty('--tilt-y', '0deg');
    };

    const handlePointerDown = (event) => {
      const element = event.target.closest(pressableSelector);
      if (!element) return;

      const rect = element.getBoundingClientRect();
      element.style.setProperty('--press-x', `${event.clientX - rect.left}px`);
      element.style.setProperty('--press-y', `${event.clientY - rect.top}px`);
      element.classList.add('is-pressing');

      window.setTimeout(() => {
        element.classList.remove('is-pressing');
      }, 360);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerleave', handlePointerLeave, true);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerleave', handlePointerLeave, true);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [location.pathname, language]);

  return null;
}
